import Style from 'ol/style/Style';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Icon from 'ol/style/Icon';
import Circle from 'ol/style/Circle';
import RegularShape from 'ol/style/RegularShape';

import { degreesToRadians } from 'cadenza/utils/math';
import { RoundedSquare } from 'cadenza/map/style/rounded-square';
import { Diamond } from 'cadenza/map/style/diamond';
import { getColorWithOpacity, shadeOf } from 'cadenza/utils/colors/colors';
import { GeometryType } from 'cadenza/map/geom/geometry-type';
import { routeWayPointStyleProvider } from 'cadenza/map/style/style-providers/route-way-point';
import { routeStepLineStyleProvider } from 'cadenza/map/style/style-providers/route-line-geometry';
import { getSize } from 'cadenza/map/style/style-util';
import { createLineOlStyle, createStrokeForLine } from 'cadenza/map/style/create-line-style';
import { ovalRenderer } from 'cadenza/map/geom/oval-renderer';
import { FillSignatureType, FillStyle, PointSignatureType, StyleProperty } from 'cadenza/map/style/style-constants';

export const DEFAULT_OL_Z_INDEX = 10;

/**
 * @callback styleProvider
 * @param {object} style - A style DTO
 * @param {GeometryType} geometryType - A feature geometry type
 * @return {ol.style.Style|Function} Return a style object if the provider support the given style, or undefined otherwise.
 */

/**
 * Specific style providers registry.
 *
 * @type {styleProvider[]}
 */
export const STYLE_PROVIDERS = [
  routeWayPointStyleProvider,
  routeStepLineStyleProvider
];

/**
 * Creates an OL style object from a style DTO
 *
 * @param {object} style - A style DTO
 * @param {object} options - optional parameters
 * @param {GeometryType} options.geometryType - The geometry type for which to create the OL Style
 * @return {(ol.style.Style|ol.style.Style[]|Function)} An OL style object or an array of style objects
 * @throws Throws an error if the DTO does not have a pointSignatureType property
 *     (only point styles are supported right now)
 */
export function createStyle (style, { geometryType = GeometryType.POINT } = {}) {
  const olStyle = createOlStyleForGeometryType(style, geometryType);
  olStyle.hashCode = style.hashCode;
  return olStyle;
}

function createOlStyleForGeometryType (style, geometryType) {
  // resolve to a specific style
  const externalStyle = STYLE_PROVIDERS
    .map(styleProvider => styleProvider(style, geometryType))
    .find(providedStyle => !!providedStyle);
  if (externalStyle) {
    return externalStyle;
  }
  // resolve to a standard style
  switch (geometryType) {
    case GeometryType.POINT:
    case GeometryType.MULTI_POINT:
      return createPointOlStyle(style);
    case GeometryType.LINE:
    case GeometryType.LINE_STRING:
    case GeometryType.MULTI_LINE_STRING:
      return createLineOlStyle(style);
    case GeometryType.OVAL:
      return createOvalOlStyle(style);
    default:
      return createPolygonOlStyle(style);
  }
}

function createPointOlStyle (style) {
  switch (style.pointSignatureType) {
    case PointSignatureType.CIRCLE:
      return createCircle(style);
    case PointSignatureType.DIAMOND:
      return createDiamond(style);
    case PointSignatureType.TRIANGLE:
      return createTriangle(style);
    case PointSignatureType.SQUARE:
      return createSquare(style);
    case PointSignatureType.ROUNDED_SQUARE:
      return createRoundedSquare(style);
    case PointSignatureType.IMAGE:
    case PointSignatureType.CHARACTER:
      return createImage(style);
    default:
      throw new Error(`Cannot create OL point style for an unknown point signature type '${style.pointSignatureType}'`);
  }
}

function createOvalOlStyle (style) {
  const olStyle = createPolygonOlStyle(style);
  olStyle.setRenderer(ovalRenderer());
  return olStyle;
}

function createPolygonOlStyle (style) {
  // In case of a polygon, the borderColor is the foreground color for the line
  // Since borderColor has implicit alpha value,
  // we have to reset the foreground color opacity
  const strokeStyle = Object.assign({}, style, {
    [StyleProperty.FOREGROUND_COLOR]: style.borderColor,
    [StyleProperty.FOREGROUND_COLOR_OPACITY]: undefined
  });
  return new Style({
    fill: createFill(style),
    stroke: createStrokeForLine(strokeStyle),
    zIndex: style[StyleProperty.Z_INDEX] ?? DEFAULT_OL_Z_INDEX
  });
}

function createFill (style) {
  return new Fill({ color: getFillColor(style) });
}

export function getFillColor (style) {
  const fillStyle = style[StyleProperty.FILL_STYLE];
  const foregroundColor = style[StyleProperty.FOREGROUND_COLOR];
  const backgroundColor = style[StyleProperty.BACKGROUND_COLOR];

  if (fillStyle === FillStyle.TRANSPARENT) {
    return null;
  }

  if (style[StyleProperty.FILL_SIGNATURE_TYPE] === FillSignatureType.IMAGED) {
    return createCanvasWithImagePattern(style[StyleProperty.IMAGE], style[StyleProperty.ROTATION]);
  }

  if (fillStyle !== FillStyle.SOLID && Object.values(FillStyle).includes(fillStyle)) {
    return createCanvasPattern(fillStyle, foregroundColor, backgroundColor);
  }

  return createForegroundColor(style);
}

// On the backend the patterns are created in FillPaintFactory::createPaint
function createCanvasPattern (fillStyle, foregroundColor, backgroundColor) {
  switch (fillStyle) {
    case FillStyle.HORIZONTAL:
      return createHorizontalCanvasPattern(foregroundColor, backgroundColor);
    case FillStyle.HORIZONTAL_WIDE:
      return createHorizontalCanvasPattern(foregroundColor, backgroundColor, { factor: 2 });
    case FillStyle.HORIZONTAL_NARROW:
      return createHorizontalCanvasPattern(foregroundColor, backgroundColor, { factor: 0.5 });
    case FillStyle.VERTICAL:
      return createVerticalCanvasPattern(foregroundColor, backgroundColor);
    case FillStyle.VERTICAL_WIDE:
      return createVerticalCanvasPattern(foregroundColor, backgroundColor, { factor: 2 });
    case FillStyle.VERTICAL_NARROW:
      return createVerticalCanvasPattern(foregroundColor, backgroundColor, { factor: 0.5 });
    case FillStyle.DIAGONAL_UP:
      return createDiagonalUpCanvasPattern(foregroundColor, backgroundColor);
    case FillStyle.DIAGONAL_UP_WIDE:
      return createDiagonalUpCanvasPattern(foregroundColor, backgroundColor, { factor: 2 });
    case FillStyle.DIAGONAL_UP_NARROW:
      return createDiagonalUpCanvasPattern(foregroundColor, backgroundColor, { factor: 0.5 });
    case FillStyle.DIAGONAL_DOWN:
      return createDiagonalDownCanvasPattern(foregroundColor, backgroundColor);
    case FillStyle.DIAGONAL_DOWN_WIDE:
      return createDiagonalDownCanvasPattern(foregroundColor, backgroundColor, { factor: 2 });
    case FillStyle.DIAGONAL_DOWN_NARROW:
      return createDiagonalDownCanvasPattern(foregroundColor, backgroundColor, { factor: 0.5 });
    case FillStyle.CROSS:
      return createCrossCanvasPattern(foregroundColor, backgroundColor, { fillStyle });
    case FillStyle.CROSS_WIDE:
      return createCrossCanvasPattern(foregroundColor, backgroundColor, { factor: 2 });
    case FillStyle.CROSS_NARROW:
      return createCrossCanvasPattern(foregroundColor, backgroundColor, { factor: 0.5 });
    case FillStyle.DIAGONAL_CROSS:
      return createDiagonalCrossCanvasPattern(foregroundColor, backgroundColor);
    case FillStyle.DIAGONAL_CROSS_WIDE:
      return createDiagonalCrossCanvasPattern(foregroundColor, backgroundColor, { factor: 2 });
    case FillStyle.DIAGONAL_CROSS_NARROW:
      return createDiagonalCrossCanvasPattern(foregroundColor, backgroundColor, { factor: 0.5 });
    case FillStyle.GRAY_WIDE:
      return createCanvasWithPointPattern([ [ 0, 1 ] ], foregroundColor, backgroundColor, { width: 4, height: 4 });
    case FillStyle.LIGHT_GRAY:
      return createCanvasWithPointPattern([ [ 2, 0 ], [ 0, 1 ] ], foregroundColor, backgroundColor, { width: 4, height: 3 });
    case FillStyle.GRAY:
      return createCanvasWithPointPattern([ [ 0, 1 ], [ 1, 0 ] ], foregroundColor, backgroundColor, { width: 2, height: 2 });
    case FillStyle.DARK_GRAY:
      return createCanvasWithPointPattern([ [ 0, 1 ], [ 1, 0 ], [ 1, 1 ], [ 2, 0 ], [ 3, 0 ], [ 3, 1 ] ], foregroundColor, backgroundColor, {
        width: 4,
        height: 2
      });
    default:
      throw new Error(`Unsupported fillStyle: ${fillStyle}`);
  }
}

function createForegroundColor (style) {
  if (style.foregroundColor && typeof style.foregroundColorOpacity === 'number') {
    return getColorWithOpacity(style.foregroundColor, style.foregroundColorOpacity);
  }
  return style.foregroundColor;
}

function createStroke (style) {
  // compute the color if requested
  const borderColor = style.borderColorComputed
    ? shadeOf(style.foregroundColor)
    : style.borderColor;
  if (style.lineWidth && style.lineWidth > 0) {
    return new Stroke({
      color: borderColor,
      width: getSize(style.lineWidth, style.lineWidthUnit)
    });
  } else {
    return null;
  }
}

function createCircle (style) {
  return new Style({
    image: new Circle({
      fill: createFill(style),
      radius: getSize(style.symbolSize, style.symbolSizeUnit) / 2,
      stroke: createStroke(style)
    }),
    zIndex: style[StyleProperty.Z_INDEX] ?? DEFAULT_OL_Z_INDEX
  });
}

function createDiamond (style) {
  return new Style({
    image: new Diamond({
      fill: createFill(style),
      stroke: createStroke(style),
      radius: getSize(style.symbolSize, style.symbolSizeUnit) / 2,
      rotation: degreesToRadians(style.rotation)
    }),
    zIndex: style[StyleProperty.Z_INDEX] ?? DEFAULT_OL_Z_INDEX
  });
}

function createTriangle (style) {
  return new Style({
    image: new RegularShape({
      fill: createFill(style),
      stroke: createStroke(style),
      points: 3,
      radius: getSize(style.symbolSize, style.symbolSizeUnit) * 0.66,
      angle: 0,
      rotation: degreesToRadians(style.rotation)
    }),
    zIndex: style[StyleProperty.Z_INDEX] ?? DEFAULT_OL_Z_INDEX
  });
}

function createSquare (style) {
  return new Style({
    image: new RegularShape({
      fill: createFill(style),
      stroke: createStroke(style),
      points: 4,
      radius: getSize(style.symbolSize, style.symbolSizeUnit) / Math.sqrt(2),
      angle: degreesToRadians(45),
      rotation: degreesToRadians(style.rotation)
    }),
    zIndex: style[StyleProperty.Z_INDEX] ?? DEFAULT_OL_Z_INDEX
  });
}

function createRoundedSquare (style) {
  return new Style({
    image: new RoundedSquare({
      fill: createFill(style),
      stroke: createStroke(style),
      radius: getSize(style.symbolSize, style.symbolSizeUnit) / 2,
      rotation: degreesToRadians(style.rotation)
    }),
    zIndex: style[StyleProperty.Z_INDEX] ?? DEFAULT_OL_Z_INDEX
  });
}

function createImage (style) {
  return new Style({
    image: new Icon({
      src: style[StyleProperty.IMAGE_AS_BASE64],
      rotation: style.rotation ? degreesToRadians(style.rotation) : undefined,
      // for images (if required) we use symbolSize to scale it
      scale: style.symbolSize || 1
    }),
    zIndex: style[StyleProperty.Z_INDEX] ?? DEFAULT_OL_Z_INDEX
  });
}

function createHorizontalCanvasPattern (foregroundColor, backgroundColor, options = {}) {
  const lines = [ [ 0, 3, 6, 3 ] ];
  return createCanvasWithLinePattern(lines, foregroundColor, backgroundColor, { canvasSize: 6, ...options });
}

function createVerticalCanvasPattern (foregroundColor, backgroundColor, options = {}) {
  const lines = [ [ 3, 0, 3, 6 ] ];
  return createCanvasWithLinePattern(lines, foregroundColor, backgroundColor, { canvasSize: 6, ...options });
}

function createDiagonalUpCanvasPattern (foregroundColor, backgroundColor, options = {}) {
  const lines = [ [ 6, 0, 0, 6 ], [ 6, 12, 12, 6 ] ];
  return createCanvasWithLinePattern(lines, foregroundColor, backgroundColor, { canvasSize: 12, ...options });
}

function createDiagonalDownCanvasPattern (foregroundColor, backgroundColor, options = {}) {
  const lines = [ [ 6, 0, 12, 6 ], [ 6, 12, 0, 6 ] ];
  return createCanvasWithLinePattern(lines, foregroundColor, backgroundColor, { canvasSize: 12, ...options });
}

function createCrossCanvasPattern (foregroundColor, backgroundColor, options) {
  const lines = [ [ 0, 0, 0, 6 ], [ 0, 0, 6, 0 ] ];
  return createCanvasWithLinePattern(lines, foregroundColor, backgroundColor, { canvasSize: 6, ...options });
}

function createDiagonalCrossCanvasPattern (foregroundColor, backgroundColor, options) {
  const lines = [ [ 0, 0, 12, 12 ], [ 0, 12, 12, 0 ] ];
  return createCanvasWithLinePattern(lines, foregroundColor, backgroundColor, { canvasSize: 12, ...options });
}

function createCanvasWithLinePattern (lines, foregroundColor, backgroundColor, { canvasSize, factor = 1 } = {}) {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  canvas.width = canvasSize * factor;
  canvas.height = canvasSize * factor;

  context.fillStyle = backgroundColor;
  context.fillRect(0, 0, canvas.width, canvas.height);

  context.strokeStyle = foregroundColor;
  context.beginPath();
  lines.forEach(([ startX, startY, endX, endY ]) => {
    context.moveTo(startX * factor, startY * factor);
    context.lineTo(endX * factor, endY * factor);
  });
  context.stroke();

  return context.createPattern(canvas, 'repeat');
}

function createCanvasWithPointPattern (points, foregroundColor, backgroundColor, { width, height } = {}) {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  canvas.width = width;
  canvas.height = height;

  context.fillStyle = backgroundColor;
  context.fillRect(0, 0, canvas.width, canvas.height);

  // Reset the fillStyle
  context.fillStyle = foregroundColor;
  context.beginPath();
  points.forEach(([ x, y ]) => {
    context.fillRect(x, y, 1, 1);
  });
  context.stroke();

  return context.createPattern(canvas, 'repeat');
}

function createCanvasWithImagePattern (htmlImageElement, rotation) {
  const canvas = document.createElement('canvas');
  canvas.width = htmlImageElement.width;
  canvas.height = htmlImageElement.height;

  const context = canvas.getContext('2d');
  context.drawImage(htmlImageElement, 0, 0);

  const canvasPattern = context.createPattern(canvas, 'repeat');
  if (rotation && rotation !== 360) {
    // Afaik, you cannot achieve this with the context.rotate because you would always leave empty space around the rotated image
    canvasPattern.setTransform(new DOMMatrix(`rotate(${rotation}deg)`));
  }
  return canvasPattern;
}
